import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cpu,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Play,
  Copy,
  Check,
  RefreshCw,
  Terminal,
  Clock,
  Sparkles,
  Film,
  Music,
  Radio,
  Zap,
  X
} from 'lucide-react';
import {
  DaemonData,
  fetchDaemonStatus,
  triggerDaemonBake,
  pingDaemon
} from '../../services/inspectService';

interface DaemonMonitorTabProps {
  onRefreshAll?: () => void;
}

export const DaemonMonitorTab: React.FC<DaemonMonitorTabProps> = ({ onRefreshAll }) => {
  const [daemonData, setDaemonData] = useState<DaemonData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPinging, setIsPinging] = useState(false);
  const [pingFeedback, setPingFeedback] = useState<{ success: boolean; text: string } | null>(null);
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);
  const [bakingClipIds, setBakingClipIds] = useState<Set<string>>(new Set());
  const [bakeResultMsg, setBakeResultMsg] = useState<{ id: string; msg: string; success: boolean } | null>(null);
  const isFetchingRef = useRef(false);

  const loadData = useCallback(async (fresh: boolean = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const data = await fetchDaemonStatus(fresh);
      if (data) {
        setDaemonData(data);
        if (data.daemon?.state === 'online') {
          // If daemon is online, clear any stale error notice
          setPingFeedback(prev => prev && !prev.success ? null : prev);
        }
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCmd(id);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  const handleTriggerBake = async (clipId: string) => {
    setBakingClipIds(prev => new Set(prev).add(clipId));
    setBakeResultMsg(null);
    try {
      const res = await triggerDaemonBake(clipId);
      setBakeResultMsg({
        id: clipId,
        msg: res.message || res.error || (res.success ? 'Job submitted' : 'Failed to trigger'),
        success: res.success
      });
      await loadData();
      if (onRefreshAll) onRefreshAll();
    } finally {
      setBakingClipIds(prev => {
        const next = new Set(prev);
        next.delete(clipId);
        return next;
      });
    }
  };

  const handlePingDaemon = async () => {
    if (isPinging) return;
    setIsPinging(true);
    setPingFeedback({ success: true, text: 'Broadcasting ping challenge to local/docker daemon... Waiting for next 5s poll cycle.' });

    try {
      const sendRes = await pingDaemon();
      if (!sendRes.success) {
        setPingFeedback({ success: false, text: sendRes.error || 'Failed to dispatch ping challenge' });
        setIsPinging(false);
        return;
      }

      const reqTime = sendRes.requestedAt || Date.now();
      const startTime = performance.now();
      let attempts = 0;
      const maxAttempts = 6; // poll every 2s, max 12s total

      const pollAck = async () => {
        attempts++;
        const latest = await fetchDaemonStatus(true);
        if (latest) {
          setDaemonData(latest);
          const hbTs = latest.daemon?.heartbeat?.timestamp || 0;
          if (hbTs >= reqTime - 2000 || latest.daemon?.state === 'online') {
            const latencyMs = Math.round(performance.now() - startTime);
            setPingFeedback({
              success: true,
              text: `Daemon Verified Online! Host: ${latest.daemon.heartbeat.hostname} (Acknowledged in ${(latencyMs / 1000).toFixed(1)}s)`
            });
            setIsPinging(false);
            if (onRefreshAll) onRefreshAll();
            setTimeout(() => {
              setPingFeedback(prev => prev?.success ? null : prev);
            }, 8000);
            return;
          }
        }

        if (attempts < maxAttempts) {
          setTimeout(pollAck, 2000);
        } else {
          setPingFeedback({
            success: false,
            text: 'Daemon did not respond within 12 seconds. Is scraper_daemon running locally or in Docker?'
          });
          setIsPinging(false);
        }
      };

      setTimeout(pollAck, 2000);
    } catch (err: any) {
      setPingFeedback({ success: false, text: err.message || 'Ping failed' });
      setIsPinging(false);
    }
  };

  const state = daemonData?.daemon?.state || 'offline';
  const heartbeat = daemonData?.daemon?.heartbeat;
  const lastSeenSeconds = daemonData?.daemon?.lastSeenSeconds || 999999;

  const stateColor =
    state === 'online' ? 'var(--accent-emerald)' :
    state === 'baking' ? 'var(--accent-amber)' :
    state === 'stale' ? '#fb923c' : 'var(--accent-coral)';

  const stateLabel =
    state === 'online' ? 'Online (Verified)' :
    state === 'baking' ? 'Active (Baking Clips)' :
    state === 'stale' ? 'Standby (Last seen >1h ago)' : 'Offline / Standby';

  return (
    <div>
      {/* Daemon Status Card */}
      <div style={{
        padding: '1.25rem 1.5rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: `1px solid ${stateColor}40`,
        marginBottom: pingFeedback ? '1rem' : '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1.25rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            padding: '12px',
            borderRadius: 'var(--radius-md)',
            background: `${stateColor}20`,
            color: stateColor,
            boxShadow: `0 0 16px ${stateColor}35`
          }}>
            <Cpu size={24} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: stateColor,
                boxShadow: `0 0 8px ${stateColor}`
              }} />
              <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                {stateLabel}
              </h2>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '3px' }}>
              Host: <strong>{heartbeat?.hostname || 'Unknown'}</strong> • Version: {heartbeat?.version || '1.2'} •
              {lastSeenSeconds < 3600
                ? ` Last response: ${lastSeenSeconds}s ago`
                : ' No recent response'}
              {' '}<span style={{ opacity: 0.7 }}>(On-Demand Mode)</span>
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={handlePingDaemon}
            disabled={isPinging || isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              background: 'rgba(16, 185, 129, 0.15)',
              color: 'var(--accent-emerald)',
              cursor: isPinging ? 'not-allowed' : 'pointer',
              fontSize: '0.82rem',
              fontWeight: 700,
              boxShadow: isPinging ? '0 0 12px rgba(16, 185, 129, 0.3)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <Radio size={14} className={isPinging ? 'spin' : ''} />
            <span>{isPinging ? 'Pinging Daemon...' : 'Check Online Status'}</span>
          </button>

          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={isLoading || isPinging}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-glass)',
              background: 'var(--bg-glass)',
              color: 'var(--text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.82rem',
              fontWeight: 600
            }}
            title="Refresh cached status"
          >
            <RefreshCw size={13} className={isLoading ? 'spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {pingFeedback && (!(!pingFeedback.success && state === 'online')) && (
        <div style={{
          marginTop: '-0.5rem',
          marginBottom: '2rem',
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          background: pingFeedback.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${pingFeedback.success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          color: pingFeedback.success ? 'var(--accent-emerald)' : 'var(--accent-coral)',
          fontSize: '0.8rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {pingFeedback.success ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <span>{pingFeedback.text}</span>
          </div>
          <button
            type="button"
            onClick={() => setPingFeedback(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '2px 4px',
              opacity: 0.7,
              display: 'flex',
              alignItems: 'center'
            }}
            title="Dismiss notification"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Queue Metrics Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={metricCardStyle}>
          <div style={{ padding: '8px', borderRadius: 'var(--radius-md)', background: 'rgba(139, 92, 246, 0.2)', color: 'var(--accent-purple)' }}>
            <Clock size={18} />
          </div>
          <div>
            <div style={metricValStyle}>{daemonData?.queueSummary?.totalPending ?? 0}</div>
            <div style={metricLblStyle}>Clips in Queue</div>
          </div>
        </div>

        <div style={metricCardStyle}>
          <div style={{ padding: '8px', borderRadius: 'var(--radius-md)', background: 'rgba(239, 68, 68, 0.2)', color: 'var(--accent-coral)' }}>
            <Film size={18} />
          </div>
          <div>
            <div style={metricValStyle}>{daemonData?.queueSummary?.missingVideoCount ?? 0}</div>
            <div style={metricLblStyle}>Missing MP4 Video</div>
          </div>
        </div>

        <div style={metricCardStyle}>
          <div style={{ padding: '8px', borderRadius: 'var(--radius-md)', background: 'rgba(245, 158, 11, 0.2)', color: 'var(--accent-amber)' }}>
            <Music size={18} />
          </div>
          <div>
            <div style={metricValStyle}>{daemonData?.queueSummary?.missingStemsCount ?? 0}</div>
            <div style={metricLblStyle}>Missing Demucs Stems</div>
          </div>
        </div>

        <div style={metricCardStyle}>
          <div style={{ padding: '8px', borderRadius: 'var(--radius-md)', background: 'rgba(16, 185, 129, 0.2)', color: 'var(--accent-emerald)' }}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div style={metricValStyle}>{heartbeat?.bakedTotal ?? 0}</div>
            <div style={metricLblStyle}>Total Baked by Daemon</div>
          </div>
        </div>
      </div>

      {/* Bake Result Notice */}
      {bakeResultMsg && (
        <div style={{
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          background: bakeResultMsg.success ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${bakeResultMsg.success ? 'var(--accent-emerald)' : 'var(--accent-coral)'}`,
          color: '#fff',
          fontSize: '0.82rem',
          marginBottom: '1rem'
        }}>
          Clip <code>{bakeResultMsg.id}</code>: {bakeResultMsg.msg}
        </div>
      )}

      {/* Unbaked Queue Table */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
            Unbaked Processing Queue
          </h3>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Clips waiting for residential YouTube capture or GPU stem separation
          </span>
        </div>

        <div style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-glass)',
          background: 'var(--bg-card)',
          overflow: 'hidden'
        }}>
          {isLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <p>Scanning queue status...</p>
            </div>
          ) : !daemonData?.queue || daemonData.queue.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--accent-emerald)' }}>
              <CheckCircle2 size={28} style={{ marginBottom: '8px' }} />
              <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>Queue Clean & Ready</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>All captured clips are fully baked with MP4 video and Demucs stems.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.25)' }}>
                    <th style={thStyle}>Clip Title & ID</th>
                    <th style={thStyle}>Performer</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Video MP4</th>
                    <th style={thStyle}>Vocals Stem</th>
                    <th style={thStyle}>BGM Stem</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {daemonData.queue.map((item, idx) => {
                    const isBaking = bakingClipIds.has(item.id);
                    return (
                      <tr key={item.id} style={{ borderTop: idx > 0 ? '1px solid var(--border-glass)' : 'none' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 700, color: '#fff' }}>{item.title}</div>
                          <code style={{ fontSize: '0.7rem', color: 'var(--accent-purple)', fontFamily: 'var(--font-mono)' }}>
                            {item.id}
                          </code>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            background: item.userName === 'Orphaned' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                            color: item.userName === 'Orphaned' ? 'var(--accent-amber)' : 'var(--accent-purple)',
                            fontSize: '0.72rem',
                            fontWeight: 600
                          }}>
                            {item.userName}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {Math.round(item.duration)}s
                        </td>
                        <td style={tdStyle}>
                          {item.missingVideo ? (
                            <span style={{ color: 'var(--accent-coral)', fontSize: '0.75rem', fontWeight: 600 }}>Missing MP4</span>
                          ) : (
                            <span style={{ color: 'var(--accent-emerald)', fontSize: '0.75rem' }}>Ready</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {item.missingVocals ? (
                            <span style={{ color: 'var(--accent-amber)', fontSize: '0.75rem', fontWeight: 600 }}>Missing Vocals</span>
                          ) : (
                            <span style={{ color: 'var(--accent-emerald)', fontSize: '0.75rem' }}>Ready</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {item.missingBgm ? (
                            <span style={{ color: 'var(--accent-amber)', fontSize: '0.75rem', fontWeight: 600 }}>Missing BGM</span>
                          ) : (
                            <span style={{ color: 'var(--accent-emerald)', fontSize: '0.75rem' }}>Ready</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            disabled={isBaking}
                            onClick={() => handleTriggerBake(item.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 12px',
                              borderRadius: 'var(--radius-sm)',
                              border: 'none',
                              background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-coral) 100%)',
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              cursor: isBaking ? 'not-allowed' : 'pointer',
                              opacity: isBaking ? 0.6 : 1
                            }}
                          >
                            <Play size={11} />
                            <span>{isBaking ? 'Baking...' : 'Bake via Modal'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Terminal Quick-Commands Guide */}
      <div style={{
        padding: '1.25rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
          <Terminal size={18} color="var(--accent-cyan)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', margin: 0 }}>
            Launch Scraper Daemon Locally
          </h3>
        </div>

        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.5 }}>
          The scraper daemon uses residential IP connections with <code>yt-dlp</code> and dispatches audio to Modal Labs NVIDIA T4 GPUs for Demucs stem separation (~2 seconds).
        </p>

        <div style={{ display: 'grid', gap: '10px' }}>
          {[
            {
              id: 'cmd-local',
              title: 'Option A: Continuous Local Python Runner',
              cmd: 'python scripts/scraper_daemon.py'
            },
            {
              id: 'cmd-once',
              title: 'Option B: Single Sweep Over Unbaked Clips',
              cmd: 'python scripts/scraper_daemon.py --once'
            },
            {
              id: 'cmd-docker',
              title: 'Option C: Lightweight Docker Container',
              cmd: 'docker compose up scraper_daemon'
            }
          ].map(item => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-cosmic)',
                border: '1px solid var(--border-glass)',
                gap: '12px'
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  {item.title}
                </div>
                <code style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.8rem',
                  color: 'var(--accent-cyan)',
                  wordBreak: 'break-all'
                }}>
                  {item.cmd}
                </code>
              </div>
              <button
                type="button"
                onClick={() => handleCopy(item.cmd, item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: copiedCmd === item.id ? 'var(--accent-emerald)' : 'var(--text-secondary)',
                  fontSize: '0.74rem',
                  cursor: 'pointer'
                }}
              >
                {copiedCmd === item.id ? <Check size={13} /> : <Copy size={13} />}
                <span>{copiedCmd === item.id ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const metricCardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--bg-card)',
  border: '1px solid var(--border-glass)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

const metricValStyle: React.CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: 800,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-display)',
  lineHeight: 1.1
};

const metricLblStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: '2px'
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px'
};
